import { SelectOption } from '../../searchableSelect/searchableSelectTypes';
import { getTranslationFromTranslatableEntity, MatterStatus } from '../../../constants/matter';
import { IMatter } from '../../../interface/matter';
import { ITransaction } from '../../../interface/transaction';
import { ITransactionTypes } from '../../../interface/transactionTypes';

export const MATTER_SELECTION_PAGE_SIZE = 50;

/** The shape of a picked matter, also used when a matter is preselected without the picker. */
export const MATTER_SELECTION_FIELDS = {
  withFavoriteInformation: true,
  withDefaultTransactionType: true,
  withHasLinkedTransactionTypes: true,
  relations: ['principalCompany:name', 'principalPrivatePerson:fullName'],
  fields: [
    'subject',
    'matterNumber',
    'status',
    'invoiceableType',
    'openingDate',
    'hasImportantNotes',
    'currency',
    'language',
  ],
};

/** What both the picker page and the dropdown ask for, so a picked matter has the same shape either way. */
export const MATTER_SELECTION_QUERY = {
  ...MATTER_SELECTION_FIELDS,
  extraFilters: { status: MatterStatus.ACTIVE },
};

export const DEFAULT_TRANSACTION_TYPE_FIELDS = [
  'unitPrice',
  'standardPrice',
  'units',
  'translations',
  'transactionTypeUsage',
];

interface MatterSelectionContext {
  defaultTransactionType: ITransactionTypes | null | undefined;
  transactionTypeInUse: boolean | undefined;
  appLanguage: string;
}

export const buildMatterSelection = (
  transaction: ITransaction | null,
  matter: IMatter,
  { defaultTransactionType, transactionTypeInUse, appLanguage }: MatterSelectionContext,
) =>
  ({
    ...transaction,
    matter,
    matterId: matter?.id,
    isManualUnitPrice: false,
    isManualSkill: false,
    shouldLoadServerPrice: true,
    principalCompany: matter?.principalCompany,
    principalPrivatePerson: matter?.principalPrivatePerson,
    overrideWarnings: true,
    shouldFetchDefaultTransactionType: false,
    ...(matter?.defaultTransactionTypeId &&
      transactionTypeInUse && {
        transactionType: defaultTransactionType,
        transactionTypeId: defaultTransactionType?.id,
        unitId: defaultTransactionType?.unitId,
        unit: defaultTransactionType?.unit,
        ...(!transaction?.isSubjectSetByUser &&
          !transaction?.isFromMailOrAppointment &&
          !transaction?.timerId && {
            currentSubject: getTranslationFromTranslatableEntity(
              defaultTransactionType?.translations,
              matter?.language ?? appLanguage,
            ),
          }),
      }),
  }) as ITransaction;

interface PrincipalSource {
  principalCompany?: { name?: string } | null;
  principalPrivatePerson?: { fullName?: string } | null;
}

export const getPrincipalName = (source?: PrincipalSource | null) =>
  source?.principalCompany?.name || source?.principalPrivatePerson?.fullName || '';

export const toMatterOption = (
  matter: Pick<IMatter, 'id' | 'matterNumber' | 'subject'>,
  principalName: string,
): SelectOption => {
  const numberAndSubject = `${matter.matterNumber} - ${matter.subject}`;
  return principalName
    ? { value: matter.id, label: principalName, description: numberAndSubject }
    : { value: matter.id, label: numberAndSubject };
};
